import React from 'react';
import _ from 'underscore';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import { Formatters, Messages } from '../../codegen/Localize';
import { openReactAlertDialog } from '../../commonInfra/dialogs/ReactAlertDialog';
import { ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { AuthorizableType, getAuthorizableType } from '../../permissions/types/AuthorizableType';
import { L10n } from '../../ts/L10n/L10n';
import { IDataSourceInfo } from '../../ts/models/IDataSourceInfo';
import { Set } from '../../ts/models/Set';
import { INamedItem } from '../../ts/ServerApi/types/resourceParts';
import { ConnectedPermissionsDialog, PermissionsDialogOwnProps } from '../components/PermissionsDialog';
import { GetActionableObjectsForAction } from '../requests/GetActionableObjectsForAction';
import { IGetActionableObjectsResult } from '../requests/types/GetActionableObjectsRequest';
import { PermissionsDialogStyles } from '../styles/PermissionsDialogStyle';

interface MessagesForEditPermissionsAction {
  permissionsDialogTitle_single: (a: { ITEM_NAME: string }) => string;
  permissionsDialogTitle_multiple: (a: { ITEM_COUNT: number, ITEM_COUNT_LOC: string }) => string;
  cannotShowPermissions: (a: { ITEM_NAME: string, ITEM_COUNT: number, ITEM_COUNT_LOC: string }) => string;
}

// Using separate strings for single items and multiple items because
// CJK languages don't have plural form so the displayed message could look werid (defect 1158040)
const AuthorizableTypeToMessagesMap = new Map<AuthorizableType, MessagesForEditPermissionsAction>()
  .set(AuthorizableType.COLLECTION, {
    permissionsDialogTitle_single: Messages.permissionsForCollection_single,
    permissionsDialogTitle_multiple: Messages.permissionsForCollection_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedCollections,
  })
  .set(AuthorizableType.DATABASE, {
    permissionsDialogTitle_single: Messages.permissionsForDatabase_single,
    permissionsDialogTitle_multiple: Messages.permissionsForDatabase_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedDatabases
  })
  .set(AuthorizableType.DATAROLE, {
    permissionsDialogTitle_single: Messages.permissionsForDataRole_single,
    permissionsDialogTitle_multiple: Messages.permissionsForDataRole_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedDataRoles
  })
  .set(AuthorizableType.DATASOURCE, {
    permissionsDialogTitle_single: Messages.permissionsForDataSource_single,
    permissionsDialogTitle_multiple: Messages.permissionsForDataSource_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedDataSources,
  })
  .set(AuthorizableType.FLOW, {
    permissionsDialogTitle_single: Messages.permissionsForFlow_single,
    permissionsDialogTitle_multiple: Messages.permissionsForFlow_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedFlows,
  })
  .set(AuthorizableType.LENS, {
    permissionsDialogTitle_single: Messages.permissionsForLens_single,
    permissionsDialogTitle_multiple: Messages.permissionsForLens_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedLenses,
  })
  .set(AuthorizableType.METRIC, {
    permissionsDialogTitle_single: Messages.permissionsForMetric_single,
    permissionsDialogTitle_multiple: Messages.permissionsForMetric_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedMetrics,
  })
  .set(AuthorizableType.PUBLISHED_CONNECTION, {
    permissionsDialogTitle_single: Messages.permissionsForPublishedConnection_single,
    permissionsDialogTitle_multiple: Messages.permissionsForPublishedConnection_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedPublishedConnections,
  })
  .set(AuthorizableType.PROJECT, {
    permissionsDialogTitle_single: Messages.permissionsForProject_single,
    permissionsDialogTitle_multiple: Messages.permissionsForProject_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedProjects,
  })
  .set(AuthorizableType.TABLE, {
    permissionsDialogTitle_single: Messages.permissionsForTable_single,
    permissionsDialogTitle_multiple: Messages.permissionsForTable_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedTables,
  })
  .set(AuthorizableType.VIEW, {
    permissionsDialogTitle_single: Messages.permissionsForView_single,
    permissionsDialogTitle_multiple: Messages.permissionsForView_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedViews,
  })
  .set(AuthorizableType.WORKBOOK, {
    permissionsDialogTitle_single: Messages.permissionsForWorkbook_single,
    permissionsDialogTitle_multiple: Messages.permissionsForWorkbook_multiple,
    cannotShowPermissions: Messages.permissions_cannotViewSelectedWorkbooks,
  });

export const ReactPermissionsDialogTestId = 'permissions-dialog-react';

export namespace EditPermissionsAction {

  export function showEditPermissionsDialog(items: Set<any>, contentType: string): Promise<any> {
    // AuthorizableType is just an enum used across permissions dialog code;
    // contentType and authorizableType are essentially the same concept.
    const authorizableType = getAuthorizableType(contentType);

    if (authorizableType === AuthorizableType.DATASOURCE && areAnyEmbeddedDatasourcesSelected(items)) {
      return Promise.resolve(alertCannotShowPermissions(
        Messages.permissions_notShowingEmbeddedAndPublishedDataSources({
          ITEM_COUNT_LOC: Formatters.formatCount(items.size()),
        })
      ));
    }

    return GetActionableObjectsForAction.editPermissions({
      type: authorizableType,
      ids: items.keys(),
    }).then((result: IGetActionableObjectsResult) => {
      const ids = result.ids;
      const unactionableItems = items.clone();
      let actionableItems = new Set<INamedItem>((item: INamedItem) => item.id);
      _.each(ids, (id: string) => {
        const item = items.get(id);
        actionableItems.add(item);
        unactionableItems.remove(item);
      });

      // if there are any items in the section that the user isn't allowed to set permissions on,
      // we don't show the permissions panel
      if (unactionableItems.size() > 0) {
        return alertCannotShowPermissions(
          getCannotShowPermissionsMessage(authorizableType, items)
        ).result;
      }

      const permissionsDialogProps: PermissionsDialogOwnProps = {
        authorizables: actionableItems.clone(),
        authorizableType,
      };

      return openPermissionsDialog(permissionsDialogProps);
    });
  }

  export function openPermissionsDialog(permissionsDialogProps: PermissionsDialogOwnProps) {
    const dialogTitle = getPermissionsDialogTitle(permissionsDialogProps.authorizableType, permissionsDialogProps.authorizables);

    return ReactModalService.open({
      testId: ReactPermissionsDialogTestId,
      type: DialogType.NormalWithTitleBar,
      dialogWidth: PermissionsDialogStyles.dialogContainerWidth,
      dialogContent: React.createElement(ConnectedPermissionsDialog, permissionsDialogProps),
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      shouldReturnFocusOnClose: true,
      title: dialogTitle,
    });
  }

  export function getPermissionsDialogTitle(authorizableType: AuthorizableType, authorizables: Set<any>): string {
    const messages = AuthorizableTypeToMessagesMap.get(authorizableType);

    if (messages && authorizables) {
      const itemCount = authorizables.size();
      return itemCount === 1
       ? messages.permissionsDialogTitle_single({ ITEM_NAME: authorizables.first().name })
       : messages.permissionsDialogTitle_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: Formatters.formatCount(itemCount) });
    }

    return Messages.permissions();
  }

  function alertCannotShowPermissions(message: string) {
    return openReactAlertDialog({
      title: Messages.permissions_cannotViewPermissions(),
      message,
      dialogWidth: TabDialogWidth.width500px
    });
  }

  function getCannotShowPermissionsMessage(authorizableType: AuthorizableType, unactionableItems: Set<INamedItem>): string {
    const messages = AuthorizableTypeToMessagesMap.get(authorizableType);

    if (messages) {
      const itemCount = unactionableItems.size();
      return messages.cannotShowPermissions({
        ITEM_NAME: L10n.elideIfLong(unactionableItems.first().name),
        ITEM_COUNT: itemCount,
        ITEM_COUNT_LOC: Formatters.formatCount(itemCount),
      });
    }

    return Messages.permissions_cannotViewPermissions();
  }

  function areAnyEmbeddedDatasourcesSelected(datasources: Set<INamedItem>): boolean {
    return !!datasources.find((datasource: IDataSourceInfo) => !!datasource.workbook);
  }

}

export type EditPermissionsAction = typeof EditPermissionsAction;
